import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { chatApi, type ChatMessage, type ChatSession } from '@/api/chat';
import { curatorApi } from '@/api/users';
import { useAuthStore } from '@/stores/auth';
import { useChatSocket } from '@/hooks/useChatSocket';
import { mergeMessages } from '@/lib/chatSocket';
import { lastMessagePreview } from '@/lib/chatAttachmentView';
import { ChatAttachment } from '@/components/chat/ChatAttachment';
import { humanizeError } from '@/lib/errors';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useBackHandler } from '@/lib/backButton';
import { MBlock, MChips, MEmpty, MTitle } from '@/components/mobile/MobileKit';
import { StaffBubble } from '@/components/chat/StaffBubble';
import { chatDayOf } from '@/lib/chatDay';

/**
 * Curator chat — sidebar of all student conversations + active conversation pane.
 * Re-uses chat-service WebSocket. Single curator may have many students.
 */

// Prefer the enriched real name; fall back to a short stable code from the id.
function studentLabel(session: ChatSession, index: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (session.student_name?.trim()) return session.student_name.trim();
  const code = (session.student_id ?? '').replace(/-/g, '').slice(0, 4).toUpperCase();
  return code ? t('curatorChat.studentWithCode', { code }) : t('curatorChat.studentWithIndex', { index: index + 1 });
}

// "Марафон | Группа" subtitle from the enriched session fields.
function marathonGroup(s: ChatSession): string {
  return [s.student_marathon, s.student_group].filter((x) => x && x.trim()).join(' | ');
}

export function CuratorChatPage() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const [activeId, setActiveId] = useState<string | null>(null);

  const qc = useQueryClient();
  const sessions = useQuery({ queryKey: ['curator', 'chats'], queryFn: () => chatApi.listMyChats('curator') });
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('');

  // Start a new conversation: pick from the curator's students, open (or reuse)
  // the direct dialog. Students are loaded only when the picker opens.
  const [pickerOpen, setPickerOpen] = useState(false);
  const students = useQuery({ queryKey: ['curator', 'students'], queryFn: curatorApi.listStudents, enabled: pickerOpen });
  const openM = useMutation({
    mutationFn: (studentUserId: string) => chatApi.openCuratorChat(studentUserId),
    onSuccess: (s) => {
      setPickerOpen(false);
      qc.invalidateQueries({ queryKey: ['curator', 'chats'] });
      setActiveId(s.id);
    },
  });

  // «Написать в чат» из карточки ученика (docs/CURATOR-MOBILE-DESIGN.md §4.4) приходит с ?student=<id>: открываем
  // (или находим существующий) диалог с ним сразу, а не список. Та же ручка, что у «Нового чата», — второго пути
  // создания диалога нет. Признак снимаем из адреса, чтобы «назад» и обновление страницы не открывали его снова.
  const [searchParams, setSearchParams] = useSearchParams();
  const studentParam = searchParams.get('student');
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!studentParam || openedFor.current === studentParam) return;
    openedFor.current = studentParam;
    openM.mutate(studentParam);
    setSearchParams(
      (p) => {
        p.delete('student');
        return p;
      },
      { replace: true },
    );
  }, [studentParam, openM, setSearchParams]);

  const all = sessions.data ?? [];
  // Distinct groups present in the curator's chats — feeds the filter dropdown.
  const groups = useMemo(() => {
    const m = new Map<string, string>();
    all.forEach((s) => { if (s.student_group_id && s.student_group) m.set(s.student_group_id, s.student_group); });
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [all]);
  const filtered = all.filter((s) => {
    if (groupFilter && s.student_group_id !== groupFilter) return false;
    if (search.trim() && !(s.student_name ?? '').toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  });

  const activeIndex = sessions.data?.findIndex((s) => s.id === activeId) ?? -1;
  const activeSession = activeIndex >= 0 ? sessions.data![activeIndex] : null;

  const isMobile = useIsMobile();
  // На телефоне диалог — отдельный экран поверх списка (§4.8). «Назад» Android и свайп iPhone из диалога ведут к
  // списку, а не прочь из чата: тот же стек обработчиков, что у окон (`lib/backButton.ts`).
  const backToList = useCallback(() => {
    setActiveId(null);
    return true;
  }, []);
  useBackHandler(backToList, isMobile && !!activeSession);

  // «Новый чат»: свои ученики, пометка «есть чат». На телефоне — во весь экран.
  const picker = (
    <Modal open={pickerOpen} onClose={() => setPickerOpen(false)} title={t('curatorChat.newChat')} size="sm" phoneFull>
      {students.isLoading ? (
        <p className="py-4 text-center text-sm text-on-surface-variant">{t('common.loading')}</p>
      ) : students.isError ? (
        <p className="py-4 text-center text-sm text-error">{humanizeError(students.error)}</p>
      ) : students.data && students.data.length > 0 ? (
        <ul className={cn('space-y-1', !isMobile && 'max-h-[50vh] overflow-y-auto')}>
          {students.data.map((st) => {
            const has = all.some((s) => s.student_id === st.user_id);
            return (
              <li key={st.user_id}>
                <button
                  disabled={openM.isPending}
                  onClick={() => openM.mutate(st.user_id)}
                  className="flex min-h-11 w-full items-center gap-3 rounded-button px-3 py-2 text-left transition hover:bg-surface-container-low disabled:opacity-50"
                >
                  <Avatar name={st.full_name} src={st.avatar_url} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-on-surface">{st.full_name || st.email || st.user_id.slice(0, 6)}</p>
                    {st.email && <p className="truncate text-xs text-on-surface-variant">{st.email}</p>}
                  </div>
                  {has && <span className="shrink-0 text-[11px] text-on-surface-variant">{t('curatorChat.hasChat')}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="py-4 text-center text-sm text-on-surface-variant">{t('curatorChat.noStudents')}</p>
      )}
      {openM.isError && <p className="mt-2 text-xs text-error">{humanizeError(openM.error)}</p>}
    </Modal>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.8): два экрана, как у ученика — список и диалог ─────────────
  if (isMobile) {
    if (activeSession) {
      return (
        <ConversationPane
          key={activeSession.id}
          session={activeSession}
          label={studentLabel(activeSession, activeIndex, t)}
          meId={me?.id ?? ''}
          onBack={() => setActiveId(null)}
        />
      );
    }
    return (
      <div className="space-y-3">
        <MTitle
          title={t('curatorDashboard.chats')}
          right={
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              aria-label={t('curatorChat.newChat')}
              className="grid h-11 w-11 flex-none place-items-center rounded-full bg-primary text-on-primary"
            >
              <span className="material-symbols-outlined text-[22px]">add</span>
            </button>
          }
        />
        <label className="flex items-center gap-2 rounded-full bg-surface-container-low px-4">
          <span className="material-symbols-outlined text-[18px] text-on-surface-variant">search</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('curatorChat2.searchByName')}
            className="h-11 w-full bg-transparent text-[14px] text-on-surface outline-none placeholder:text-on-surface-variant"
          />
        </label>
        {groups.length > 0 && (
          <MChips
            chips={[{ key: '', label: t('curatorChat2.allGroups') }, ...groups.map((g) => ({ key: g.id, label: g.name }))]}
            active={groupFilter}
            onChange={setGroupFilter}
          />
        )}
        {sessions.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : sessions.isError ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(sessions.error)}</p>
        ) : !filtered.length ? (
          <MEmpty icon="chat" title={all.length ? t('curatorChat2.nobodyFound') : t('curatorChat.noChats')} />
        ) : (
          <MBlock>
            {filtered.map((s, i) => {
              const label = studentLabel(s, i, t);
              const mg = marathonGroup(s);
              const preview = lastMessagePreview(s.last_message, t);
              return (
                <button
                  key={s.id}
                  type="button"
                  data-chat-row
                  onClick={() => setActiveId(s.id)}
                  className={cn('flex w-full items-center gap-3 py-3 text-left', i < filtered.length - 1 && 'border-b border-outline')}
                >
                  <Avatar name={label} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-semibold text-on-surface">{label}</span>
                    {mg && <span className="block truncate text-[11px] font-medium text-primary">{mg}</span>}
                    <span className="block truncate text-[11.5px] text-on-surface-variant">
                      {preview || t('curatorChat.noMessages')}
                    </span>
                  </span>
                  {s.unread_count ? (
                    <span className="min-w-[22px] flex-none rounded-full bg-primary px-1.5 text-center text-[11px] font-bold leading-[22px] text-on-primary">
                      {s.unread_count}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </MBlock>
        )}
        {picker}
      </div>
    );
  }

  return (
    <div className="grid h-[calc(100vh-10rem)] grid-cols-1 gap-4 md:grid-cols-[280px_1fr]">
      {/* Conversation list */}
      <Card className="overflow-hidden p-0">
        <div className="flex items-start justify-between gap-2 px-4 pt-4">
          <PageHeader title={t('curatorChat.students')} className="p-0" />
          <Button size="sm" variant="secondary" onClick={() => setPickerOpen(true)} className="mt-0.5 shrink-0">
            <span className="material-symbols-outlined text-base">add</span>
            {t('curatorChat.newChat')}
          </Button>
        </div>
        {/* Search by name + filter by group */}
        <div className="space-y-2 px-4 pb-2">
          <div className="relative">
            <span className="material-symbols-outlined pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-base text-on-surface-variant">search</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('curatorChat2.searchByName')}
              className="w-full rounded-button bg-surface-container-low py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          {groups.length > 0 && (
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="w-full rounded-button bg-surface-container-low px-3 py-2 text-sm outline-none"
            >
              <option value="">{t('curatorChat2.allGroups')}</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          )}
        </div>
        {sessions.isLoading && (
          <p className="px-4 py-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
        )}
        {sessions.isError && (
          <p className="px-4 py-3 text-sm text-error">{humanizeError(sessions.error)}</p>
        )}
        {!sessions.isLoading && !sessions.isError && !filtered.length && (
          <p className="px-4 py-3 text-sm text-on-surface-variant">
            {all.length ? t('curatorChat2.nobodyFound') : t('curatorChat.noChats')}
          </p>
        )}
        <ul className="overflow-y-auto">
          {filtered.map((s, i) => {
            const label = studentLabel(s, i, t);
            const mg = marathonGroup(s);
            // Сообщение только с файлом — «Вложение: имя», а не «Нет сообщений» (техдолг 389).
            const preview = lastMessagePreview(s.last_message, t);
            return (
              <li key={s.id}>
                <button
                  onClick={() => setActiveId(s.id)}
                  className={cn('flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-surface-container-low', activeId === s.id && 'bg-primary-fixed')}
                >
                  <Avatar name={label} />
                  <div className="flex-1 truncate">
                    <p className="truncate font-semibold text-on-surface">{label}</p>
                    {mg && <p className="truncate text-[11px] font-medium text-primary">{mg}</p>}
                    {preview ? (
                      <p className="truncate text-xs text-on-surface-variant">{preview}</p>
                    ) : (
                      <p className="truncate text-xs text-on-surface-variant">{t('curatorChat.noMessages')}</p>
                    )}
                  </div>
                  {s.unread_count ? (
                    <span className="rounded-pill bg-primary px-2 py-0.5 text-xs text-on-primary">{s.unread_count}</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </Card>

      {/* Active conversation */}
      {activeSession ? (
        <ConversationPane
          // Другой диалог — другая лента: без `key` история нового влилась бы в сообщения прежнего.
          key={activeSession.id}
          session={activeSession}
          label={studentLabel(activeSession, activeIndex, t)}
          meId={me?.id ?? ''}
        />
      ) : (
        <Card variant="subtle" className="flex items-center justify-center text-on-surface-variant">
          {t('curatorChat.selectStudent')}
        </Card>
      )}

      {/* New-chat picker: start a dialog with any assigned student */}
      {picker}
    </div>
  );
}

function ConversationPane({
  session,
  label,
  meId,
  onBack,
}: {
  session: ChatSession;
  label: string;
  meId: string;
  /** Есть — телефонный экран диалога со своим «назад» к списку (§4.8). */
  onBack?: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [draft, setDraft] = useState('');
  const [sendErr, setSendErr] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // История по REST: при открытии диалога, перед каждым переподключением сокета и сразу после него
  // (`lib/chatSocket.ts`) — так приходит то, что было написано, пока соединения не было.
  const catchUp = useCallback(async () => {
    try {
      const fetched = await chatApi.getHistory(session.id);
      setMessages((m) => mergeMessages(m, fetched));
      // Opening the conversation clears the unread badge in the sidebar.
      chatApi.markRead(session.id, []).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['curator', 'chats'] });
    } finally {
      setLoadingHistory(false);
    }
  }, [session.id, queryClient]);

  const onEvent = useCallback(
    (parsed: unknown) => {
      // Drop ephemeral typing/presence events and service frames (read, error) —
      // they are not chat messages and must not be rendered as empty bubbles.
      const evt = parsed as { type?: string };
      if (evt.type && evt.type !== 'message') return;

      const msg = parsed as ChatMessage;
      if (msg.room_id !== session.id || !msg.id) return;
      setMessages((m) => (m.some((mm) => mm.id === msg.id) ? m : [...m, msg]));
    },
    [session.id],
  );

  // Сокет сам переподключается после обрыва, возврата на вкладку и смены сети; адрес — от адреса API.
  const socket = useChatSocket({ roomId: session.id, catchUp, onEvent });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!draft.trim()) return;
    const content = draft.trim();
    setDraft('');
    setSendErr(null);
    try {
      // The server echoes the message back to us over the socket.
      if (!socket.send({ room_id: session.id, content })) {
        const saved = await chatApi.sendMessage({ room_id: session.id, content, role: 'curator' });
        setMessages((m) => (m.some((mm) => mm.id === saved.id) ? m : [...m, saved]));
      }
    } catch (e) {
      // Restore the draft so the curator does not lose the text on failure.
      setDraft(content);
      setSendErr(humanizeError(e));
    }
  };

  // ── Телефон: экран диалога по образцу ученика (StudentChatPage) ──────────────────────────────────────────
  // ⚠️ `.chat-screen`: в приложении экран привязан к краям окна (`html.native .chat-screen` в index.css), клавиатура
  // сжимает окно, нижнее меню при ней прячется — поле стоит над клавиатурой, лента прокручивается к последнему.
  // До 13.10.2026 поле ввода куратора на 390 уходило под нижнее меню (сверка docs/CHECK-2026-10-12-CURATOR-MOBILE.md).
  //
  // Высота на сайте — экран минус поля `.safe-main` (0,5rem сверху и 4,75rem под нижним меню, плюс безопасные зоны).
  // Число ученика (9,25rem) здесь не годится: у ученика над чатом своя шапка, у сотрудника её нет, и на кадре 390
  // между полем и меню оставалась пустая полоса ~75 px (стенд, 13.10.2026).
  if (onBack) {
    return (
      <div className="chat-screen flex h-[calc(100dvh-5.25rem-var(--sa-top)-var(--sa-bottom))] flex-col">
        <div className="flex flex-none items-center gap-2.5 pb-3 pt-1.5">
          <button
            type="button"
            onClick={onBack}
            aria-label={t('curatorDashboard.chats')}
            className="grid h-11 w-11 flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
          >
            <span className="material-symbols-outlined text-[18px]">chevron_left</span>
          </button>
          <Avatar name={label} className="h-10 w-10 flex-none text-[13px]" />
          <div className="min-w-0 flex-1 truncate font-display text-[16px] font-bold text-on-surface">{label}</div>
        </div>
        <div data-chat-feed className="flex flex-1 flex-col gap-2.5 overflow-y-auto pb-2">
          {loadingHistory ? (
            <p className="text-center text-[13px] text-on-surface-variant">{t('curatorChat.loadingMessages')}</p>
          ) : messages.length === 0 ? (
            <p className="m-auto max-w-[240px] text-center text-[12.5px] text-on-surface-variant">
              {t('curatorChat.emptyConversation')}
            </p>
          ) : (
            messages.map((m, i) => (
              <StaffBubble
                key={m.id}
                msg={m}
                mine={m.sender_id === meId}
                showDay={chatDayOf(m) !== chatDayOf(messages[i - 1])}
              />
            ))
          )}
          <div ref={bottomRef} />
        </div>
        {sendErr && <p className="flex-none pb-1 text-[11px] text-error">{sendErr}</p>}
        <div data-chat-composer className="flex flex-none items-center gap-2">
          <div className="flex flex-1 items-center rounded-full bg-surface-container-low px-4 py-3">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
              placeholder={t('curatorChat.messagePlaceholder')}
              className="min-w-0 flex-1 bg-transparent text-[13.5px] text-on-surface placeholder:text-on-surface-variant focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={send}
            disabled={!draft.trim()}
            aria-label={t('common.send')}
            className="grid h-[46px] w-[46px] flex-none place-items-center rounded-full bg-primary text-on-primary disabled:opacity-45"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <Card className="flex flex-col p-0">
      <div className="flex items-center gap-3 border-b border-outline-variant/20 p-4">
        <Avatar name={label} size="sm" />
        <p className="font-semibold">{label}</p>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-4">
        {loadingHistory ? (
          <p className="text-center text-sm text-on-surface-variant">{t('curatorChat.loadingMessages')}</p>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-on-surface-variant">{t('curatorChat.emptyConversation')}</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={cn('flex', m.sender_id === meId ? 'justify-end' : 'justify-start')}>
              <div className={cn('max-w-[70%] rounded-card px-4 py-2 text-sm', m.sender_id === meId ? 'bg-primary-container text-on-primary-container' : 'bg-surface-container-low text-on-surface')}>
                {/* Файлы ученика (1.1.1 и сайт): до 12.10.2026 здесь был только текст — фото без подписи давало пустой пузырь. */}
                {m.attachments?.map((a, i) => <ChatAttachment key={a.id ?? `${a.url}-${i}`} attachment={a} />)}
                {m.content && <p className="whitespace-pre-wrap break-words">{m.content}</p>}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-outline-variant/20 p-3">
        {sendErr && <p className="mb-2 text-xs text-error">{sendErr}</p>}
        <div className="flex gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
            placeholder={t('curatorChat.messagePlaceholder')}
            className="flex-1 rounded-button bg-surface-container-lowest px-4 py-2 text-on-surface shadow-soft transition placeholder:text-on-surface-variant/60 focus:shadow-ambient focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <Button onClick={send} disabled={!draft.trim()}>
            <span className="material-symbols-outlined">send</span>
          </Button>
        </div>
      </div>
    </Card>
  );
}
